import { countryCityPlaceholder } from "./countries";
import React, { useEffect, useRef, useState } from "react";
import { BackButton, Button, Card, Chip, Input, SectionTitle, Select, Textarea } from "./ui";
import api from "./api";

/* -------------------------------------------------------------------------- */
/* My Dogs — list + add form + edit modal                                     */
/* -------------------------------------------------------------------------- */

const emptyForm = {
  name: "",
  breed: "",
  breed_key: "",
  breed_other: "",
  age: "",
  sex: "Macho",
  size: "Mediano",
  weight: "",
  color: "",
  health: "Sin enfermedades",
  illnessDetail: "",
  vaccines: true,
  lastVetCheck: "",
  dewormed: true,
  sterilized: false,
  pedigree: "No sé",
  temperament: "",
  compatibility: "",
  location: "",
  available: true,
  heatCycle: "",
  notes: "",
  image: "",
};

const defaultPhotos = [];

function dogToForm(dog) {
  return {
    ...emptyForm,
    ...dog,
    age: dog.age != null ? String(dog.age) : "",
    temperament: Array.isArray(dog.temperament)
      ? dog.temperament.join(", ")
      : dog.temperament || "",
  };
}

export default function MyDogs({
  dogs,
  onAdd,
  onUpdate,
  onDelete,
  onOpenPassport,
  onBack,
  defaultLocation = "",
  countryCode = "PA",
}) {
  const [showForm, setShowForm] = useState(false);
  const [editingDog, setEditingDog] = useState(null); // dog object or null
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [breedCatalog, setBreedCatalog] = useState([]);
  const [breedCatalogError, setBreedCatalogError] = useState("");
  const [photoFiles, setPhotoFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [photoError, setPhotoError] = useState("");
  const fileInputRef = useRef(null);
  const MAX_PHOTOS = 4;

  useEffect(() => {
    let active = true;

    api.breeds
      .list()
      .then((items) => {
        if (!active) return;
        setBreedCatalog(Array.isArray(items) ? items : []);
        setBreedCatalogError("");
      })
      .catch(() => {
        if (!active) return;
        setBreedCatalogError("No se pudo cargar el catálogo de razas.");
      });

    return () => {
      active = false;
    };
  }, []);

  const resetPhoto = () => {
    setPhotoFiles([]);
    setPreviews([]);
    setPhotoError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const okTypes = ["image/jpeg", "image/png", "image/webp"];
    const okExt = /\.(jpe?g|png|webp)$/i;
    const accepted = [];
    for (const file of files) {
      if (!okTypes.includes(file.type) || !okExt.test(file.name)) {
        setPhotoError("Formato no válido. Usa JPG, PNG o WEBP.");
        continue;
      }
      if (file.size > 5 * 1024 * 1024) {
        setPhotoError("Cada imagen debe pesar máximo 5 MB.");
        continue;
      }
      accepted.push(file);
    }
    if (!accepted.length) return;
    setPhotoError("");
    setPhotoFiles((prev) => [...prev, ...accepted].slice(0, MAX_PHOTOS));
    setPreviews((prev) =>
      [...prev, ...accepted.map((f) => URL.createObjectURL(f))].slice(0, MAX_PHOTOS)
    );
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removePreview = (idx) => {
    setPhotoFiles((prev) => prev.filter((_, i) => i !== idx));
    setPreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  const isEditing = !!editingDog;

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const startAdd = () => {
    setEditingDog(null);
    setForm({
      ...emptyForm,
      location: defaultLocation || "",
    });
    setErrors({});
    resetPhoto();
    setShowForm(true);
  };

  const startEdit = (dog) => {
    setEditingDog(dog);
    setForm(dogToForm(dog));
    setErrors({});
    resetPhoto();
    setShowForm(true);
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const cancel = () => {
    setShowForm(false);
    setEditingDog(null);
    setForm(emptyForm);
    setErrors({});
    resetPhoto();
  };

  const submit = (ev) => {
    ev.preventDefault();
    const e = {};
    if (!form.name.trim()) e.name = "El nombre es obligatorio.";
    if (!form.breed_key) e.breed = "La raza es obligatoria.";
    if (form.breed_key === "other" && !form.breed_other.trim()) {
      e.breed = "Especifica la raza.";
    }
    if (!form.age) e.age = "Ingresa la edad.";
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    const temperament = form.temperament
      ? form.temperament.split(",").map((t) => t.trim()).filter(Boolean)
      : ["Sociable"];

    const selectedBreed = breedCatalog.find(
      (item) => item.key === form.breed_key
    );

    const breedFields =
      form.breed_key === "other"
        ? {
            breed_key: "other",
            breed: form.breed_other.trim(),
            breed_other: form.breed_other.trim(),
          }
        : {
            breed_key: form.breed_key,
            breed: selectedBreed?.name || form.breed,
            breed_other: "",
          };

    if (isEditing) {
      const updated = {
        ...editingDog,
        ...form,
        ...breedFields,
        age: Number(form.age) || 1,
        temperament,
        // Keep the original image if user did not pick a new file
        image: form.image || editingDog.image,
      };
      onUpdate(updated, photoFiles);
    } else {
      onAdd(
        {
          ...form,
          ...breedFields,
          image: form.image,
          age: Number(form.age) || 1,
          temperament,
        },
        photoFiles
      );
    }
    cancel();
  };

  return (
    <div className="mx-auto max-w-5xl px-4 pb-32 pt-4 sm:px-6" data-testid="mydogs-screen">
      <div className="mb-4 flex items-center justify-between">
        <BackButton onClick={onBack} testId="mydogs-back-btn" />
        <Button
          variant="mint"
          onClick={() => (showForm ? cancel() : startAdd())}
          testId="mydogs-toggle-form-btn"
        >
          {showForm ? "Cerrar" : "+ Agregar peludito"}
        </Button>
      </div>

      <SectionTitle eyebrow="Mi familia">Mis perros</SectionTitle>
      <p className="mt-2 text-sm text-slate-500 md:text-base">
        Administra tus huellitas registradas y mantén sus datos de salud al día.
      </p>

      {showForm && (
        <Card className="mt-5 p-5 pc-rise" data-testid={isEditing ? "mydogs-edit-form" : "mydogs-form"}>
          <p className="font-display text-xl font-black text-[#0B2D4D]">
            {isEditing ? `Editar perfil de ${editingDog.name}` : "Agregar peludito"}
          </p>
          <p className="mt-1 text-sm text-slate-500">
            {isEditing
              ? "Actualiza los datos de tu peludito. Los cambios se guardan al instante."
              : "Completa la información para empezar a encontrar compañía responsable."}
          </p>
          <form className="mt-4 space-y-4" onSubmit={submit} noValidate>
            <div>
              <label className="mb-1.5 block text-sm font-bold text-[#0B2D4D]">
                Fotos de tu peludito
              </label>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                className="hidden"
                data-testid="mydogs-photo-input"
                onChange={(e) => handleFiles(e.target.files)}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                testId="mydogs-photo-btn"
                className="w-full"
                disabled={previews.length >= MAX_PHOTOS}
              >
                📷 {previews.length ? "Agregar otra foto" : "Subir fotos / Elegir desde galería"}
              </Button>
              {photoError && (
                <p
                  className="mt-1.5 text-xs font-semibold text-rose-600"
                  data-testid="mydogs-photo-error"
                >
                  {photoError}
                </p>
              )}
              <p className="mt-1.5 text-xs text-slate-400">
                {isEditing
                  ? "Puedes agregar más fotos (se suman a las actuales)."
                  : "Sube 1 a 4 fotos · La primera será la principal."}{" "}
                JPG, PNG o WEBP · máx 5 MB c/u
              </p>
              {previews.length > 0 && (
                <div className="mt-3 grid grid-cols-3 gap-2" data-testid="mydogs-photo-previews">
                  {previews.map((src, idx) => (
                    <div
                      key={idx}
                      className="relative overflow-hidden rounded-2xl border border-slate-100"
                    >
                      <img
                        src={src || "/dog-placeholder.svg"}
                        onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/dog-placeholder.svg"; }}
                        alt={`Vista previa ${idx + 1}`}
                        className="h-24 w-full object-cover"
                        style={{ objectPosition: "center top" }}
                        data-testid={`mydogs-photo-preview-${idx}`}
                      />
                      {idx === 0 && (
                        <span className="absolute left-1 top-1 rounded-full bg-[#0B2D4D] px-2 py-0.5 text-[9px] font-bold text-white">
                          Principal
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => removePreview(idx)}
                        aria-label="Quitar foto"
                        data-testid={`mydogs-photo-remove-${idx}`}
                        className="absolute right-1 top-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/90 text-xs font-black text-rose-600 shadow"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <Input
                label="Nombre"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                error={errors.name}
                testId="mydogs-name-input"
              />
              <div>
                <Select
                  label="Raza"
                  value={form.breed_key}
                  onChange={(e) => {
                    const key = e.target.value;
                    const selected = breedCatalog.find(
                      (item) => item.key === key
                    );

                    if (key === "other") {
                      setForm((current) => ({
                        ...current,
                        breed_key: "other",
                        breed: current.breed_other || "",
                        breed_other: current.breed_other || "",
                      }));
                    } else {
                      setForm((current) => ({
                        ...current,
                        breed_key: key,
                        breed: selected?.name || "",
                        breed_other: "",
                      }));
                    }
                  }}
                  testId="mydogs-breed-select"
                >
                  <option value="">Selecciona una raza</option>
                  {breedCatalog.map((item) => (
                    <option key={item.key} value={item.key}>
                      {item.name}
                    </option>
                  ))}
                </Select>

                {form.breed_key === "other" && (
                  <div className="mt-3">
                    <Input
                      label="Especifica la raza"
                      value={form.breed_other}
                      onChange={(e) =>
                        setForm((current) => ({
                          ...current,
                          breed: e.target.value,
                          breed_other: e.target.value,
                        }))
                      }
                      testId="mydogs-breed-other-input"
                    />
                  </div>
                )}

                {errors.breed && (
                  <p className="mt-1 text-xs font-bold text-rose-600">
                    {errors.breed}
                  </p>
                )}

                {breedCatalogError && (
                  <p className="mt-1 text-xs font-bold text-rose-600">
                    {breedCatalogError}
                  </p>
                )}
              </div>
              <Input
                label="Edad (años)"
                type="number"
                min="0"
                value={form.age}
                onChange={(e) => update("age", e.target.value)}
                error={errors.age}
                testId="mydogs-age-input"
              />
              <Select
                label="Sexo"
                value={form.sex}
                onChange={(e) => update("sex", e.target.value)}
                testId="mydogs-sex-select"
              >
                <option>Macho</option>
                <option>Hembra</option>
              </Select>
              <Select
                label="Tamaño"
                value={form.size}
                onChange={(e) => update("size", e.target.value)}
                testId="mydogs-size-select"
              >
                <option>Pequeño</option>
                <option>Mediano</option>
                <option>Grande</option>
              </Select>
              <Input
                label="Peso"
                value={form.weight}
                onChange={(e) => update("weight", e.target.value)}
                placeholder="Ej. 8 kg"
                testId="mydogs-weight-input"
              />
              <Input
                label="Color"
                value={form.color}
                onChange={(e) => update("color", e.target.value)}
                placeholder="Blanco con marrón"
                testId="mydogs-color-input"
              />
              <Select
                label="Pedigrí"
                value={form.pedigree}
                onChange={(e) => update("pedigree", e.target.value)}
                testId="mydogs-pedigree-select"
              >
                <option>Sí</option>
                <option>No</option>
                <option>No sé</option>
              </Select>
              <Input
                label="Último control veterinario"
                type="date"
                value={form.lastVetCheck}
                onChange={(e) => update("lastVetCheck", e.target.value)}
                testId="mydogs-vetdate-input"
              />
              <Input
                label="Ubicación"
                value={form.location}
                onChange={(e) => update("location", e.target.value)}
                placeholder={countryCityPlaceholder(countryCode)}
                testId="mydogs-location-input"
              />
            </div>

            <Select
              label="¿Sufre alguna enfermedad?"
              value={form.health}
              onChange={(e) => update("health", e.target.value)}
              testId="mydogs-health-select"
            >
              <option>Sin enfermedades</option>
              <option>Tiene una enfermedad controlada</option>
            </Select>
            {form.health !== "Sin enfermedades" && (
              <Textarea
                label="Descripción de la enfermedad"
                rows={2}
                value={form.illnessDetail}
                onChange={(e) => update("illnessDetail", e.target.value)}
                testId="mydogs-illness-input"
              />
            )}

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <ToggleCard
                active={form.vaccines}
                onClick={() => update("vaccines", !form.vaccines)}
                title="Vacunas al día"
                emoji="💉"
                testId="mydogs-vaccines-toggle"
              />
              <ToggleCard
                active={form.dewormed}
                onClick={() => update("dewormed", !form.dewormed)}
                title="Desparasitación al día"
                emoji="🌿"
                testId="mydogs-dewormed-toggle"
              />
              <ToggleCard
                active={form.sterilized}
                onClick={() => update("sterilized", !form.sterilized)}
                title="Esterilizado"
                emoji="✂️"
                testId="mydogs-sterilized-toggle"
              />
              <ToggleCard
                active={form.available}
                onClick={() => update("available", !form.available)}
                title="Disponible para cruce"
                emoji="🐾"
                testId="mydogs-available-toggle"
              />
            </div>

            <Input
              label="Temperamento (separado por comas)"
              value={form.temperament}
              onChange={(e) => update("temperament", e.target.value)}
              placeholder="Sociable, Juguetón, Noble"
              testId="mydogs-temperament-input"
            />
            <Input
              label="Compatibilidad con otros perros"
              value={form.compatibility}
              onChange={(e) => update("compatibility", e.target.value)}
              placeholder="Se lleva bien con perros medianos"
              testId="mydogs-compat-input"
            />

            {form.sex === "Hembra" && (
              <Input
                label="Ciclo de celo"
                value={form.heatCycle}
                onChange={(e) => update("heatCycle", e.target.value)}
                placeholder="Ej. cada 6 meses"
                testId="mydogs-heat-input"
              />
            )}

            <Textarea
              label="Observaciones"
              rows={2}
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              testId="mydogs-notes-input"
            />

            {isEditing ? (
              <div className="grid grid-cols-2 gap-3">
                <Button
                  variant="outline"
                  type="button"
                  onClick={cancel}
                  testId="mydogs-cancel-btn"
                >
                  Cancelar
                </Button>
                <Button
                  variant="mint"
                  type="submit"
                  testId="mydogs-update-btn"
                >
                  Guardar cambios
                </Button>
              </div>
            ) : (
              <Button
                variant="mint"
                type="submit"
                testId="mydogs-save-btn"
                className="w-full py-4 text-base"
              >
                Guardar huellita
              </Button>
            )}
          </form>
        </Card>
      )}

      <div className="mt-6 grid gap-4 md:grid-cols-2" data-testid="mydogs-list">
        {dogs.length === 0 && (
          <button
            type="button"
            onClick={startAdd}
            data-testid="mydogs-empty-cta"
            aria-label="Registrar tu primer peludito"
            className="col-span-full w-full rounded-[24px] border border-dashed border-slate-200 bg-white p-8 text-center shadow-sm transition hover:-translate-y-0.5 hover:border-[#67D5B5] hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[#67D5B5] focus-visible:ring-offset-2"
          >
            <p className="text-4xl">🐶</p>
            <p className="mt-3 font-display text-2xl font-black text-[#0B2D4D]">
              Aún no has registrado un peludito
            </p>
            <p className="mt-2 text-sm text-slate-500">
              Agrega a tu perro para empezar a explorar compañía responsable.
            </p>
            <span className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#67D5B5] px-5 py-2.5 text-sm font-black text-[#0B2D4D] shadow-sm">
              Toca aquí para registrar tu primer peludito 🐾
            </span>
          </button>
        )}
        {dogs.map((dog) => (
          <Card key={dog.id} className="overflow-hidden" data-testid={`mydog-${dog.id}`}>
            <div className="relative h-64 w-full overflow-hidden">
              <img
                src={dog.image || "/dog-placeholder.svg"}
                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/dog-placeholder.svg"; }}
                alt={dog.name}
                className="h-full w-full object-cover"
                style={{ objectPosition: "center top" }}
              />
              <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/65 to-transparent" />
              <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between gap-2 text-white">
                <div className="min-w-0">
                  <p className="truncate font-display text-2xl font-black drop-shadow">{dog.name}</p>
                  <p className="truncate text-sm opacity-90">
                    {dog.breed} · {dog.age} años · {dog.sex}
                  </p>
                </div>
                <Chip tone={dog.vaccines ? "mint" : "yellow"} className="flex-shrink-0 whitespace-nowrap">
                  {dog.vaccines ? "💉 Vacunas al día" : "Pendiente"}
                </Chip>
              </div>
            </div>
            <div className="space-y-3 p-4">
              <div className="flex flex-wrap gap-2">
                {(dog.temperament || []).map((t) => (
                  <Chip key={t} tone="navy">{t}</Chip>
                ))}
                <Chip tone={dog.health === "Sin enfermedades" ? "mint" : "yellow"}>
                  🩺 {dog.health}
                </Chip>
                {dog.pedigree === "Sí" && <Chip tone="yellow">🏅 Pedigrí</Chip>}
              </div>
              <p className="text-sm leading-relaxed text-slate-500">
                {dog.notes || "Sin observaciones adicionales."}
              </p>
              <button
                type="button"
                onClick={() => onOpenPassport && onOpenPassport(dog)}
                data-testid={`mydog-passport-${dog.id}`}
                className="flex w-full items-center gap-3 rounded-2xl border border-[#0B2D4D]/10 bg-[#0B2D4D] p-3 text-left text-white transition hover:bg-[#153d63] active:translate-y-[1px]"
              >
                <span className="text-2xl">🐾</span>
                <span className="min-w-0 flex-1">
                  <span className="block font-black leading-tight">Pasaporte PawCita</span>
                  <span className="block text-xs text-white/80">Salud, vacunas e historial de tu peludito.</span>
                </span>
                <span className="text-lg" aria-hidden="true">›</span>
              </button>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  testId={`mydog-edit-${dog.id}`}
                  onClick={() => startEdit(dog)}
                >
                  Editar perfil
                </Button>
                {dog.id !== "bruce-001" ? (
                  <Button
                    variant="danger"
                    onClick={() => onDelete(dog.id)}
                    testId={`mydog-delete-${dog.id}`}
                  >
                    Eliminar
                  </Button>
                ) : (
                  <div className="inline-flex items-center justify-center rounded-full border border-emerald-100 bg-emerald-50 px-3 py-3 text-xs font-bold text-emerald-700">
                    Perfil principal
                  </div>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function ToggleCard({ active, onClick, title, emoji, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-pressed={active}
      className={`flex items-center gap-3 rounded-2xl border p-4 text-left text-sm font-bold transition ${
        active
          ? "border-emerald-200 bg-emerald-50 text-emerald-800"
          : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"
      }`}
    >
      <span className="flex-shrink-0 text-xl">{emoji}</span>
      <span className="min-w-0 flex-1 break-words leading-snug">{title}</span>
      <span
        className={`flex-shrink-0 h-5 w-9 rounded-full transition ${
          active ? "bg-emerald-400" : "bg-slate-200"
        }`}
        aria-hidden="true"
      >
        <span
          className={`block h-5 w-5 rounded-full bg-white shadow transition ${
            active ? "translate-x-4" : "translate-x-0"
          }`}
        />
      </span>
    </button>
  );
}
